import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Blueprints } from './pages/Blueprints';
import { WorkflowBuilder } from './pages/WorkflowBuilder';
import { Integrations } from './pages/Integrations';
import { ActiveDirectoryPage } from './pages/domains/ActiveDirectoryPage';
import { VirtualizationPage } from './pages/domains/VirtualizationPage';
import { SecurityPage } from './pages/domains/SecurityPage';
import { StoragePage } from './pages/domains/StoragePage';
import { NetworkPage } from './pages/domains/NetworkPage';
import { PatchManagementPage } from './pages/domains/PatchManagementPage';
import { useAuthStore } from './store/auth.store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Lazy placeholder pages
const WorkflowsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white mb-2">My Workflows</h1>
    <p className="text-white/50">Manage and monitor your automation workflows.</p>
  </div>
);

const ActivityPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white mb-2">Activity & Execution Log</h1>
    <p className="text-white/50">Full execution history and real-time monitoring.</p>
  </div>
);

const AIPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white mb-2">AI Assistant</h1>
    <p className="text-white/50">Incident analysis, policy advisor, and natural language workflow generation.</p>
  </div>
);

const AuditPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white mb-2">Audit Log</h1>
    <p className="text-white/50">Immutable audit trail for all platform actions.</p>
  </div>
);

const SettingsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
    <p className="text-white/50">Tenant configuration, RBAC, and platform settings.</p>
  </div>
);

const DomainPage = ({ domain }: { domain: string }) => (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-white mb-2 capitalize">{domain} Domain</h1>
    <p className="text-white/50">Domain-specific automation workflows and blueprints.</p>
  </div>
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="blueprints" element={<Blueprints />} />
            <Route path="builder" element={<WorkflowBuilder />} />
            <Route path="workflows" element={<WorkflowsPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="ai" element={<AIPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/profile" element={<SettingsPage />} />
            <Route path="domains/ad" element={<ActiveDirectoryPage />} />
            <Route path="domains/security" element={<SecurityPage />} />
            <Route path="domains/vm" element={<VirtualizationPage />} />
            <Route path="domains/storage" element={<StoragePage />} />
            <Route path="domains/network" element={<NetworkPage />} />
            <Route path="domains/patch" element={<PatchManagementPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
